// 캐릭터별 로컬 키 규칙(docs/CHARACTER-PRD.md §5·§13.1). 순수(DOM 없음) — Worker·시험도 쓴다.
// 규칙: 진행 키(account/keys.ts SYNC_KEYS) 전부와 그 사본(.bak·.good …)은 캐릭터별이다.
//   원래 키 'dungeonhero.<이름>' → 캐릭터 키 'dungeonhero.char.<캐릭터 id>.<이름>'  (예: dungeonhero.char.c_…​.save.v1.good)
// 저장 모듈은 키 문자열을 바꾸지 않는다: CharacterStorage가 저장소 한 곳에서 키를 바꿔 끼운다(save.ts·daily.ts의 기본 저장소).
import { COPY_SUFFIXES, SYNC_KEYS, type KV } from '../account/keys';
import { isCharacterId } from './protocol';

/** 캐릭터 키 접두사 */
export const CHAR_KEY_PREFIX = 'dungeonhero.char.';
/** 이관 스냅샷 대기 키 접두사(claim별 불변 — online.ts stagingKey). 서버에 올리지 않는 기기 전용 */
export const MIGRATION_STAGING_PREFIX = 'dungeonhero.charmig.';
const BASE_PREFIX = 'dungeonhero.';
/** 캐릭터별 진행 키 = 동기화 진행 키 전부(MMO식: 계정에 남는 진행 키는 없다 — 계정 창고·설정·소셜은 다른 키/서버) */
export const CHARACTER_SCOPED_KEYS: readonly string[] = SYNC_KEYS;

const SCOPED = new Set(CHARACTER_SCOPED_KEYS);
/** 캐릭터 키로 바꿔야 하는 키인가(진행 키 또는 그 사본) */
export function isScopedKey(key: string): boolean {
  if (SCOPED.has(key)) return true;
  for (const s of COPY_SUFFIXES) if (key.endsWith(s) && SCOPED.has(key.slice(0, -s.length))) return true;
  return false;
}

/** 원래 키 → 그 캐릭터의 키(진행 키가 아니면 그대로) */
export function charKey(id: string, key: string): string {
  if (!isScopedKey(key)) return key;
  return CHAR_KEY_PREFIX + id + '.' + key.slice(BASE_PREFIX.length);
}

/** 캐릭터 키 → {id, 원래 키}(캐릭터 키가 아니면 null) */
export function parseCharKey(key: string): { id: string; key: string } | null {
  if (!key.startsWith(CHAR_KEY_PREFIX)) return null;
  const rest = key.slice(CHAR_KEY_PREFIX.length);
  const dot = rest.indexOf('.');
  if (dot < 0) return null;
  const id = rest.slice(0, dot), base = BASE_PREFIX + rest.slice(dot + 1);
  return isCharacterId(id) && isScopedKey(base) ? { id, key: base } : null;
}

/** 그 캐릭터의 모든 로컬 키 목록(진행 키 + 사본) — 삭제·이관 확인용 */
export function allCharKeys(id: string): string[] {
  const out: string[] = [];
  for (const k of CHARACTER_SCOPED_KEYS) for (const s of ['', ...COPY_SUFFIXES]) out.push(charKey(id, k + s));
  return out;
}

/** 실제 저장 영역(localStorage 또는 시험용 메모리 — removeItem이 없을 수 있다) */
export interface StorageArea { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem?(key: string): void }

/**
 * 캐릭터 저장소: 진행 키(와 사본)는 그 캐릭터 키로, 나머지(설정·계정·이 기능 자신)는 그대로 넘긴다.
 * 저장 모듈(SaveStore·GearStore·…)과 동기화 엔진(account/sync.ts)이 원래 키 이름으로 읽고 쓰는 그대로 캐릭터별이 된다.
 * storage 이벤트 판정은 keyFor·area로 한다(game/profile.ts·gear.ts — storageEventMatches).
 */
export class CharacterStorage implements KV {
  constructor(readonly area: StorageArea, readonly characterId: string) {}
  keyFor(key: string): string { return charKey(this.characterId, key); }
  getItem(key: string): string | null { return this.area.getItem(this.keyFor(key)); }
  setItem(key: string, value: string): void { this.area.setItem(this.keyFor(key), value); }
  removeItem(key: string): void { this.area.removeItem?.(this.keyFor(key)); }
}

/** storage 이벤트가 이 저장소의 그 키(원래 이름)에 해당하는가. 캐릭터 저장소면 실제 저장 영역과 바뀐 키 이름으로 비교한다 */
export function storageEventMatches(storage: unknown, event: { storageArea: unknown; key: string | null }, ...keys: string[]): boolean {
  const cs = storage instanceof CharacterStorage ? storage : null;
  const area = cs ? cs.area : storage;
  if (event.storageArea && event.storageArea !== area) return false;
  if (event.key === null) return true;
  for (const k of keys) if (event.key === (cs ? cs.keyFor(k) : k)) return true;
  return false;
}

/** 캐릭터 동기화 자신의 키(account/sync.ts SyncKeys 꼴: 메타·당겨온 스냅샷 대기·적용 전 사본). 진행 키가 아니라 서버에 올리지 않는다 */
export function charSyncKeys(id: string): { meta: string; apply: string; backup: string } {
  const p = CHAR_KEY_PREFIX + id + '.';
  return { meta: p + 'sync.v1', apply: p + 'sync.apply.v1', backup: p + 'sync.backup.v1' };
}
/** 설치 중 표시 키(online.ts installVerified): 설치 시작 때 먼저 쓰고 설치·검증이 끝나야 지운다. 남아 있으면(쓰기 실패·탭 종료) 그 캐릭터는 받는 중·업로드 금지 */
export function charInstallingKey(id: string): string { return CHAR_KEY_PREFIX + id + '.sync.installing.v1'; }
/** 설치가 끝나지 않은 캐릭터인가(표시가 남음 — 읽을 수 없으면 안전하게 true) */
export function isInstalling(kv: { getItem(k: string): string | null }, id: string): boolean {
  try { return kv.getItem(charInstallingKey(id)) !== null; } catch { return true; }
}
